// MACHINERY V2 (Stroje) — SR detail: the centre column (M2.6).
//
// Spec: data package §4 / §8 (read model). Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `SrDetailScreen` centre column
// (lines 335-386) + `SrHeaderStrip` (:418-452) + `SrFooterActions` (:492-513).
// Artboards 0700 / 0701, top to bottom:
//   header strip (mono number · status · source · resolution · urgency)
//   card: H1 · POPIS PROBLÉMU · TECHNICKÉ POZNÁMKY · PRÍLOHY
//   green RIEŠENIE panel (solved only)
//   footer action row
//
// >>> THE STATUS AND SOURCE CHIPS ARE THE M2.5 ONES, IMPORTED. <<<
// `SrStatusChip` / `SrSourceChip` come from profile/tabs/srChips.tsx, so the
// list and the detail cannot disagree about a report — and the detail inherits
// FIX 1 (sourceType 4 renders as MIDAS, not "Maintenance Plan") for free.
//
// EMPTY BODIES SPEAK. v1 prints `srDetail.noDescription` rather than hiding the
// section (srDetail/SrProblemCard.tsx:92) — an empty description is a
// fact about the report, and a missing section reads as a rendering bug. Notes
// keep v1's opposite rule: the section is omitted entirely when empty (:98).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  ServiceReport,
  ServiceReportFile,
  ServiceReportStatusCode,
} from "../../models/ServiceReportsModel";
import { FleetMenuAction } from "../fleet/FleetCardMenu";
import { SrSourceChip, SrStatusChip } from "../profile/tabs/srChips";
import { CheckGlyph, DocGlyph, NoteGlyph, PaperclipGlyph } from "../forms/FormGlyphs";
import SrDetailActions from "./SrDetailActions";
import SrDetailFiles from "./SrDetailFiles";
import { SrResolutionChip, SrUrgencyChip } from "./srDetailChips";

const Section: React.FC<{
  glyph: React.ReactNode;
  eyebrow: string;
  children: React.ReactNode;
}> = ({ glyph, eyebrow, children }) => (
  <div style={{ marginBottom: 18, minWidth: 0 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
      <span style={{ color: "var(--mod-accent)", display: "inline-flex" }}>{glyph}</span>
      <span className="eyebrow" style={{ color: "var(--mod-accent)" }}>
        {eyebrow}
      </span>
    </div>
    {children}
  </div>
);

const Body: React.FC<{ text?: string; muted?: boolean }> = ({ text, muted }) => (
  <div
    style={{
      fontSize: 13.5,
      lineHeight: 1.65,
      color: muted ? "var(--faint)" : "var(--ink-2)",
      fontStyle: muted ? "italic" : "normal",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      minWidth: 0,
    }}
  >
    {text}
  </div>
);

export interface SrDetailMainProps {
  report: ServiceReport;
  reportFiles: ServiceReportFile[];
  solvingFiles: ServiceReportFile[];
  actions: FleetMenuAction[];
  onBack: () => void;
}

const SrDetailMain: React.FC<SrDetailMainProps> = ({
  report,
  reportFiles,
  solvingFiles,
  actions,
  onBack,
}) => {
  const { translate } = useTranslation("myMachinery");
  const solved = report.statusCode === ServiceReportStatusCode.solved;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      {/* Header strip */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--mod-accent)",
            fontWeight: 600,
          }}
        >
          {translate("myMachinery:reportNo")}{" "}
          <span style={{ color: "var(--ink-2)" }}>#{report.number}</span>
        </span>
        <span style={{ flex: 1 }} />
        <SrStatusChip code={report.statusCode} />
        <SrSourceChip
          sourceType={report.sourceType}
          requestNumber={report.serviceRequestInternalNumber}
        />
        <SrResolutionChip
          openDate={report.openDate}
          closeDate={report.closeDate}
          isSolved={solved}
        />
        <SrUrgencyChip urgency={report.urgency} />
      </div>

      {/* Body card */}
      <section
        style={{
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "var(--shadow)",
          padding: "16px 18px",
          minWidth: 0,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 700,
            color: "var(--ink)",
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {report.title}
        </h1>

        <div style={{ height: 1, background: "var(--border-soft)", margin: "16px 0" }} />

        <Section glyph={<DocGlyph />} eyebrow={translate("myMachinery:srDetail.problemDescription")}>
          {report.description ? (
            <Body text={report.description} />
          ) : (
            <Body text={translate("myMachinery:srDetail.noDescription")} muted />
          )}
        </Section>

        {report.notes && (
          <Section glyph={<NoteGlyph />} eyebrow={translate("myMachinery:srDetail.technicalNotes")}>
            <Body text={report.notes} />
          </Section>
        )}

        {reportFiles.length > 0 && (
          <Section
            glyph={<PaperclipGlyph />}
            eyebrow={translate("myMachinery:srDetail.reportAttachments")}
          >
            <SrDetailFiles files={reportFiles} />
          </Section>
        )}
      </section>

      {/* RIEŠENIE — solved reports only (v1 renders the card on `isSolved`). */}
      {solved && (
        <section
          style={{
            background: "color-mix(in oklab, var(--green) 6%, var(--card))",
            border: "1px solid color-mix(in oklab, var(--green) 36%, var(--border))",
            borderLeft: "3px solid var(--green)",
            borderRadius: 12,
            padding: "14px 18px",
            minWidth: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
            <span
              style={{
                color: "color-mix(in oklab, var(--green) 80%, var(--ink))",
                display: "inline-flex",
              }}
            >
              <CheckGlyph />
            </span>
            <span
              className="eyebrow"
              style={{ color: "color-mix(in oklab, var(--green) 80%, var(--ink))" }}
            >
              {translate("myMachinery:srDetail.solution")}
            </span>
          </div>
          {report.solvingDescription ? (
            <Body text={report.solvingDescription} />
          ) : (
            <Body text={translate("myMachinery:srDetail.noSolutionDescription")} muted />
          )}
          {solvingFiles.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div
                className="eyebrow"
                style={{ color: "var(--muted)", marginBottom: 6 }}
              >
                {translate("myMachinery:srDetail.solutionAttachments")}
              </div>
              <SrDetailFiles files={solvingFiles} />
            </div>
          )}
        </section>
      )}

      <SrDetailActions actions={actions} onBack={onBack} />
    </div>
  );
};

export default SrDetailMain;
